import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import logo from '../../assets/images/logo.png'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'

type MessageState = {
  type: 'success' | 'error'
  text: string
} | null

const DeleteWebUserPage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<MessageState>(null)
  const [hasToken, setHasToken] = useState<boolean | null>(null)

  useEffect(() => {
    const token = window.sessionStorage.getItem('accessToken')
    setHasToken(Boolean(token))
  }, [])

  const handleConfirmDelete = async () => {
    const token = window.sessionStorage.getItem('accessToken')
    if (!token) {
      setMessage({
        type: 'error',
        text: 'Please log in first to delete your account.',
      })
      return
    }

    setLoading(true)
    setMessage(null)

    try {
      const baseUrl = process.env.REACT_APP_ONBOARDING_SERVICE_API || ''
      const url = `${baseUrl}/oapi/v1/auth/delete-web-user`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await response.json().catch(() => ({}))

      if (response.ok && data.success) {
        window.sessionStorage.removeItem('accessToken')
        window.sessionStorage.removeItem('refreshToken')
        window.sessionStorage.removeItem('userId')
        setMessage({
          type: 'success',
          text: data.message || 'Your account has been deleted successfully.',
        })
        setTimeout(() => navigate('/home'), 2000)
      } else {
        setMessage({
          type: 'error',
          text: data.message || 'Failed to delete your account. Please try again.',
        })
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'An error occurred. Please try again later.',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    try {
      window.close()
    } catch (e) {
      /* ignore */
    }
    window.location.href = '/home'
  }

  if (hasToken === null) {
    return null
  }

  if (hasToken === false) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          padding: 2,
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: 3,
              padding: 4,
              textAlign: 'center',
            }}
          >
            <img
              src={logo}
              alt="Transpots Logo"
              style={{ maxWidth: '200px', marginBottom: '24px' }}
            />
            <Typography variant="h5" gutterBottom>
              Delete Account
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Please log in first to delete your account.
            </Typography>
            <Button
              variant="contained"
              onClick={() => (window.location.href = '/auth/login')}
            >
              Go to Login
            </Button>
          </Box>
        </Container>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: 3,
            padding: 4,
            textAlign: 'center',
          }}
        >
          <img
            src={logo}
            alt="Transpots Logo"
            style={{ maxWidth: '200px', marginBottom: '24px' }}
          />

          <Typography variant="h4" component="h1" gutterBottom>
            Delete Account
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, mt: 1 }}
          >
            Are you sure you want to delete your account? This action cannot be
            undone and all your data will be permanently removed.
          </Typography>

          <Stack spacing={2}>
            <SubmitLockButton
              variant="contained"
              color="error"
              fullWidth
              size="large"
              onClick={handleConfirmDelete}
              disabled={loading}
              sx={{ py: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Yes, delete my account'
              )}
            </SubmitLockButton>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={handleCancel}
              disabled={loading}
              sx={{ py: 1.5 }}
            >
              Cancel
            </Button>

            {message && (
              <Alert severity={message.type} onClose={() => setMessage(null)}>
                {message.text}
              </Alert>
            )}
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}

export default DeleteWebUserPage
